import { UserButton } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Building2, CalendarClock, Coins, Compass, FileText, FileSignature, Home, Inbox, KanbanSquare, LogOut, MessageSquare, MoreHorizontal, Plus, Search, Settings, ShieldCheck, Upload, Users, X, BellRing, LifeBuoy, Wallet } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { callAction, signOut } from "../api";
import { FaradayPanel } from "../faraday/Panel";
import { formatPhone } from "../theme";
import { useLocale, useT } from "../i18n";
import type { CustomerSummary } from "@agency/core";
import { useConfig } from "./ConfigProvider";
import { PoweredBy } from "./PoweredBy";
import { Button, Sheet, cn } from "./ui";

export function Layout({ children }: { children: ReactNode }) {
  const { config, authMode } = useConfig();
  const t = useT();
  const { locale, setLocale } = useLocale();
  const location = useLocation();
  const [faradayOpen, setFaradayOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("agency.faraday");
      if (saved) return saved === "open";
      return window.innerWidth >= 1024;
    } catch {
      return true;
    }
  });
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => setMoreOpen(false), [location.pathname]);

  const toggleFaraday = () => {
    setFaradayOpen((v) => {
      try {
        localStorage.setItem("agency.faraday", v ? "closed" : "open");
      } catch {
        /* ignore */
      }
      return !v;
    });
  };

  const nav = [
    { to: "/", icon: Home, label: t("nav.today") },
    { to: "/pipelines/sales", icon: KanbanSquare, label: t("nav.pipelines"), match: "/pipelines" },
    { to: "/customers", icon: Users, label: t("nav.customers"), match: "/customers" },
    { to: "/inbox", icon: Inbox, label: t("nav.inbox") },
    { to: "/renewals", icon: CalendarClock, label: t("nav.renewals") },
    { to: "/policies", icon: ShieldCheck, label: t("nav.policies") },
    { to: "/notices", icon: BellRing, label: t("nav.notices") },
    { to: "/claims", icon: LifeBuoy, label: t("nav.claims") },
    { to: "/payments", icon: Wallet, label: t("nav.payments") },
    { to: "/forms", icon: FileSignature, label: t("nav.forms"), match: "/forms" },
    { to: "/carriers", icon: Building2, label: t("nav.carriers"), match: "/carriers" },
    { to: "/import", icon: Upload, label: t("nav.import"), match: "/import" },
    { to: "/documents", icon: FileText, label: t("nav.documents") },
    { to: "/reports", icon: BarChart3, label: t("nav.reports") },
    { to: "/commissions", icon: Coins, label: t("nav.commissions") },
    { to: "/guide", icon: Compass, label: t("nav.guide") },
  ];
  const primaryMobile = nav.slice(0, 4);
  const moreMobile = [...nav.slice(4), { to: "/settings", icon: Settings, label: t("nav.settings") }];
  const moreActive = moreMobile.some((n) => location.pathname.startsWith(n.match ?? n.to));

  const linkClass = (isActive: boolean, n: { match?: string }) =>
    cn("mx-2 my-0.5 flex min-h-tap items-center gap-3 rounded-xl px-3 font-semibold text-sm", isActive || (n.match && location.pathname.startsWith(n.match)) ? "bg-primary/12 text-primary-dark" : "text-ink/70 hover:bg-ink/5");

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 flex items-center gap-3 border-b border-ink/10 bg-white/95 backdrop-blur px-3 md:px-5 h-16">
        <NavLink to="/" className="flex items-center gap-2 shrink-0">
          <img src={config.agency.brand.logoPath} alt={config.agency.displayName} className="h-9 w-auto" />
          <span className="hidden lg:inline heading text-lg font-bold">{config.agency.displayName}</span>
        </NavLink>
        <GlobalSearch />
        <Button variant="primary" size="sm" onClick={() => navigate("/customers?new=1")} className="hidden sm:inline-flex">
          <Plus className="h-4 w-4" /> {t("header.new")}
        </Button>
        <div className="flex items-center rounded-xl border border-ink/15 overflow-hidden" role="group" aria-label={t("header.language")}>
          {config.agency.supportedLocales.map((l) => (
            <button key={l} onClick={() => setLocale(l)} className={cn("min-h-[40px] px-3 text-sm font-bold", locale === l ? "bg-ink text-white" : "text-ink/70 hover:bg-ink/5")} aria-pressed={locale === l}>
              {t(`lang.${l}`)}
            </button>
          ))}
        </div>
        {authMode === "clerk" ? (
          // Clerk's account button: profile, email, password, two-factor, active devices and sign-out, in the agency brand.
          <div className="hidden md:flex items-center gap-2 rounded-xl px-2 py-1" title={config.user.email}>
            <UserButton appearance={{ elements: { avatarBox: "h-8 w-8" } }} />
            <NavLink to="/settings" className="text-sm font-semibold leading-tight hover:underline">
              {config.user.name.split(" ")[0]}
              <span className="block text-[11px] text-ink/50 font-normal">{t(`role.${config.user.role}`)}</span>
            </NavLink>
          </div>
        ) : (
          <NavLink to="/settings" className="hidden md:flex items-center gap-2 rounded-xl px-2 py-1 hover:bg-ink/5" title={config.user.email}>
            <span className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">{initials(config.user.name)}</span>
            <span className="text-sm font-semibold leading-tight">
              {config.user.name.split(" ")[0]}
              <span className="block text-[11px] text-ink/50 font-normal">{t(`role.${config.user.role}`)}</span>
            </span>
          </NavLink>
        )}
        {config.agency.features.faradayPanel ? (
          <button onClick={toggleFaraday} aria-pressed={faradayOpen} className={cn("min-h-tap min-w-tap inline-flex items-center justify-center gap-1 rounded-xl px-2 font-semibold text-sm", faradayOpen ? "bg-primary/15 text-primary-dark" : "hover:bg-ink/5")}>
            <MessageSquare className="h-5 w-5" />
            <span className="hidden md:inline">{t("header.faraday")}</span>
          </button>
        ) : null}
      </header>

      <div className="flex flex-1 min-h-0">
        <nav className="hidden md:flex w-[208px] shrink-0 flex-col border-r border-ink/10 bg-white py-3">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => linkClass(isActive, n)}>
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
          <div className="flex-1" />
          <NavLink to="/settings" className={({ isActive }) => linkClass(isActive, {})}>
            <Settings className="h-5 w-5" /> {t("nav.settings")}
          </NavLink>
          {authMode !== "dev" ? (
            <button onClick={() => void signOut()} className={linkClass(false, {})}>
              <LogOut className="h-5 w-5" /> {t("nav.signOut")}
            </button>
          ) : null}
          <PoweredBy className="mx-5 mt-2" />
        </nav>

        <main className="flex-1 min-w-0 overflow-x-hidden pb-20 md:pb-6">
          <div className="mx-auto max-w-[1200px] px-4 py-4 md:px-6 md:py-6">{children}</div>
        </main>

        {config.agency.features.faradayPanel ? (
          <>
            <AnimatePresence initial={false}>
              {faradayOpen ? (
                <motion.aside initial={{ width: 0, opacity: 0 }} animate={{ width: 336, opacity: 1 }} exit={{ width: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="hidden lg:flex shrink-0 border-l border-ink/10 bg-white overflow-hidden">
                  <div className="w-[336px] flex flex-col h-[calc(100vh-4rem)] sticky top-16">
                    <FaradayPanel />
                  </div>
                </motion.aside>
              ) : null}
            </AnimatePresence>
            <AnimatePresence>
              {faradayOpen ? (
                <div className="lg:hidden fixed inset-0 z-50">
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink/40" onClick={toggleFaraday} />
                  <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 28, stiffness: 300 }} className="absolute inset-x-0 bottom-0 h-[80vh] rounded-t-3xl bg-white shadow-2xl flex flex-col">
                    <div className="flex justify-end px-3 pt-2">
                      <button aria-label={t("common.close")} onClick={toggleFaraday} className="min-h-tap min-w-tap inline-flex items-center justify-center rounded-xl hover:bg-ink/5">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex-1 min-h-0 flex flex-col">
                      <FaradayPanel />
                    </div>
                  </motion.div>
                </div>
              ) : null}
            </AnimatePresence>
          </>
        ) : null}
      </div>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-5 border-t border-ink/10 bg-white/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
        {primaryMobile.map((n) => (
          <NavLink key={n.to} to={n.to} className={({ isActive }) => cn("flex min-h-[56px] flex-col items-center justify-center gap-0.5 text-[11px] font-semibold", isActive || (n.match && location.pathname.startsWith(n.match)) ? "text-primary-dark" : "text-ink/60")}>
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
        <button onClick={() => setMoreOpen(true)} aria-expanded={moreOpen} className={cn("flex min-h-[56px] flex-col items-center justify-center gap-0.5 text-[11px] font-semibold", moreActive ? "text-primary-dark" : "text-ink/60")}>
          <MoreHorizontal className="h-5 w-5" /> {t("nav.more")}
        </button>
      </nav>

      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title={t("nav.more")} side="bottom">
        <ul className="grid grid-cols-2 gap-2">
          {moreMobile.map((n) => (
            <li key={n.to}>
              <NavLink to={n.to} className={({ isActive }) => cn("flex min-h-[64px] items-center gap-3 rounded-2xl border px-4 font-semibold", isActive ? "border-primary bg-primary/10 text-primary-dark" : "border-ink/10")}>
                <n.icon className="h-5 w-5" /> {n.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <PoweredBy className="mt-4 text-center" />
      </Sheet>
    </div>
  );
}

function initials(name: string) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function GlobalSearch() {
  const t = useT();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const search = useQuery({
    queryKey: ["search", q],
    queryFn: async () => (await callAction<"agency.customer.search", CustomerSummary[]>("agency.customer.search", { q, limit: 8 })).result,
    enabled: q.trim().length >= 2,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="relative flex-1 max-w-xl">
      <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
      <input
        ref={ref}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={t("header.search")}
        className="w-full min-h-[44px] rounded-xl border border-ink/15 bg-surface pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
        aria-label={t("header.search")}
      />
      {open && q.trim().length >= 2 ? (
        <div className="absolute left-0 right-0 top-full mt-1 rounded-2xl border border-ink/10 bg-white shadow-card overflow-hidden">
          {search.isLoading ? <p className="px-4 py-3 text-sm text-ink/60">{t("common.loading")}</p> : null}
          {search.data?.length === 0 ? <p className="px-4 py-3 text-sm text-ink/60">{t("customers.none")}</p> : null}
          {search.data?.map((c) => (
            <button
              key={c.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                navigate(`/customers/${c.id}`);
                setQ("");
                setOpen(false);
              }}
              className="flex w-full items-center justify-between gap-3 px-4 min-h-tap text-left hover:bg-primary/8"
            >
              <span className="font-semibold">{c.name}</span>
              <span className="text-xs text-ink/55">{formatPhone(c.phone) ?? c.city ?? ""}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
